/**
 * Unlinked mentions
 *
 * Finds places where one note mentions another note's title in plain text
 * without an actual `[[wiki-link]]`. Obsidian surfaces these next to backlinks
 * so you can discover (and optionally link) implicit references.
 *
 * The scan runs against notes' locally cached bodies (NoteRecord.content_cache),
 * so it never hits Drive.
 */

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Count plain-text mentions of `title` inside `body` that are NOT already part
 * of a `[[wiki-link]]` or `![[embed]]`.
 *
 * - Case-insensitive.
 * - Bounded by non-alphanumeric edges, so "React" doesn't match "Reactor" but
 *   does match "React." / "(React)" / "use React here".
 * - Titles shorter than 2 chars are ignored (too noisy).
 */
export function countUnlinkedMentions(title: string, body: string): number {
  const needle = title.trim()
  if (needle.length < 2 || !body) return 0

  // Drop wiki-links / embeds first so already-linked occurrences don't count.
  const stripped = body.replace(/!?\[\[[^\]]*\]\]/g, ' ')

  // Unicode-aware word boundaries: no letter/number immediately adjacent.
  const re = new RegExp(`(?<![\\p{L}\\p{N}])${escapeRegExp(needle)}(?![\\p{L}\\p{N}])`, 'giu')
  const matches = stripped.match(re)
  return matches ? matches.length : 0
}
